import { format } from 'date-fns-tz';
import { Link } from 'react-router-dom';

import IlustracaoDestaque from 'assets/img/destaque-ilustracao.png';
import { Countdown } from 'components/countdown';
import PageHeader from 'components/ui/page-header';
import { FOCO_VISIVEL } from 'utils/foco';

import { ROTA_DAS_PARTICIPACOES_DO_GESTOR } from '../rotas';
import { DATAS_DA_ETAPA, periodoDa } from './AgendaDaRealizacao';

/** Botão contornado do design ("Acompanhar minhas participações"): borda e texto `brand-blue-dark`, sem fundo. */
const BOTAO_CONTORNADO = `inline-flex items-center justify-center whitespace-nowrap rounded-jj-sm border border-brand-blue-dark bg-transparent px-[22px] py-3 text-xs font-bold uppercase tracking-[0.04em] text-brand-blue-dark no-underline hover:bg-[rgba(51,51,189,0.08)] hover:text-brand-blue-dark hover:no-underline focus:text-brand-blue-dark ${FOCO_VISIVEL}`;

/** Etapa de cada janela de `getActiveEvaluationStep` (4.1 é o Pit Stop). */
const ETAPA_DA_JANELA: Record<string, string> = {
  2: 'Validação',
  3: 'Nível 1',
  4: 'Nível 2',
  4.1: 'Pit Stop',
  5: 'Nível 3',
};

const ORDEM_DAS_ETAPAS = ['Validação', 'Nível 1', 'Nível 2', 'Pit Stop', 'Nível 3'];

export interface EtapaDaRegua {
  titulo: string;
  periodo: string | undefined;
  atual: boolean;
}

function etapaDaJanela(etapaAtiva: number | null | undefined): string | undefined {
  return typeof etapaAtiva === 'number' ? ETAPA_DA_JANELA[String(etapaAtiva)] : undefined;
}

/** Régua de etapas da fase em exibição; o Pit Stop só entra com datas, como na agenda do cartão. */
export function reguaDaFase(fase: any, etapaAtiva: number | null | undefined): EtapaDaRegua[] {
  const atual = etapaDaJanela(etapaAtiva);
  return ORDEM_DAS_ETAPAS.map(titulo => ({ titulo, periodo: periodoDa(titulo, fase), atual: titulo === atual })).filter(
    etapa => etapa.titulo !== 'Pit Stop' || !!etapa.periodo,
  );
}

/** Fim da janela de apreciação em curso na fase, no mesmo campo do calendário que a régua usa. */
export function fimDaEtapaAtiva(fase: any, etapaAtiva: number | null | undefined): number | undefined {
  const titulo = etapaDaJanela(etapaAtiva);
  const fim = titulo ? fase?.[DATAS_DA_ETAPA[titulo][1]] : undefined;
  return fim ? Number(fim) : undefined;
}

export interface TopoDoGestorProps {
  /** Pendências do fluxo projetadas pela elegibilidade (`normalPending`/`madPending`); sem valor, o título não conta. */
  pendentes?: number;
  /** Fase em exibição (`activeStageObj`). */
  fase: any;
  /** `getActiveEvaluationStep` da página. */
  etapaAtiva: number | null | undefined;
  /** Prazo que a página já mostrava com a fase aberta ("O período de apreciação desta fase inicia em"). */
  inicioDaApreciacao?: number;
}

function tituloDasPendencias(pendentes: number | undefined): string {
  if (typeof pendentes !== 'number') return 'Realizações Destaque aguardando sua apreciação';
  return pendentes === 1
    ? '1 Realização Destaque aguardando sua apreciação'
    : `${pendentes} Realizações Destaque aguardando sua apreciação`;
}

/** Cabeçalho da fila do gestor (RDN-40, RDN-52): selo do perfil, pendências, prazo e régua de etapas da fase. */
export default function TopoDoGestor({ pendentes, fase, etapaAtiva, inicioDaApreciacao }: TopoDoGestorProps) {
  const fim = fimDaEtapaAtiva(fase, etapaAtiva);
  const regua = reguaDaFase(fase, etapaAtiva);

  return (
    <>
      <PageHeader
        titulo="Realização Destaque"
        voltar={{ to: '/', destino: 'a página inicial' }}
        selo={
          <span className="rounded-jj-pill bg-brand-green px-3.5 py-[7px] font-display text-[11px] font-bold uppercase tracking-[0.05em] text-brand-navy">
            Perfil gestor validador
          </span>
        }
      />

      <section
        aria-labelledby="pendencias-do-gestor"
        className="grid grid-cols-1 items-center gap-x-6 gap-y-[22px] rounded-jj-panel bg-brand-green px-[30px] pb-[22px] pt-[26px] md:grid-cols-[minmax(0,1fr)_168px]"
      >
        <div>
          <h2
            id="pendencias-do-gestor"
            className="m-0 mb-2.5 max-w-[560px] font-display text-[25px] font-extrabold leading-[1.2] text-brand-blue-dark"
          >
            {tituloDasPendencias(pendentes)}
          </h2>

          {inicioDaApreciacao ? (
            <p className="m-0 mb-4 text-[13px] leading-[1.7] text-brand-navy">
              O período de apreciação desta fase inicia em{' '}
              <span className="inline-flex rounded-jj-pill bg-brand-yellow px-2.5 py-[3px] font-display text-xs font-bold text-brand-blue-dark">
                <Countdown deadline={new Date(inicioDaApreciacao)} urgentClassName="text-urgent" />
              </span>
            </p>
          ) : fim ? (
            <p className="m-0 mb-4 text-[13px] leading-[1.7] text-brand-navy">
              Esta etapa vai até <b>{format(new Date(fim), 'dd/MM')}</b> e encerra em{' '}
              <span className="inline-flex rounded-jj-pill bg-brand-yellow px-2.5 py-[3px] font-display text-xs font-bold text-brand-blue-dark">
                <Countdown deadline={new Date(fim)} urgentClassName="text-urgent" />
              </span>
            </p>
          ) : null}

          <Link to={ROTA_DAS_PARTICIPACOES_DO_GESTOR} className={BOTAO_CONTORNADO}>
            Acompanhar minhas participações
          </Link>
        </div>

        <img src={IlustracaoDestaque} alt="" className="hidden h-auto w-full md:block" />

        <div className="min-w-0 rounded-jj-lg bg-brand-white px-4 py-3.5 md:col-span-2">
          <h3 id="regua-da-fase" className="m-0 mb-2.5 text-[10px] font-bold uppercase tracking-[0.05em] text-content-soft">
            Fluxo de apreciação da realização
          </h3>
          <ol aria-labelledby="regua-da-fase" className="m-0 grid list-none grid-cols-[repeat(auto-fit,minmax(132px,1fr))] gap-2 p-0">
            {regua.map(etapa => (
              <li
                key={etapa.titulo}
                aria-current={etapa.atual ? 'step' : undefined}
                className={`flex min-w-0 flex-col gap-[3px] rounded-jj-md px-[11px] py-2 ${
                  etapa.atual ? 'bg-brand-blue-dark text-brand-white' : 'bg-surface-100 text-content'
                }`}
              >
                <b className="font-display text-xs leading-[1.2]">{etapa.titulo}</b>
                <span className="text-[10.5px]">{etapa.periodo ?? 'Sem datas nesta fase'}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>
    </>
  );
}
